import { Box, Grid, MenuItem, TextField } from '@mui/material';
import { Validate, ValidationGroup } from 'mui-validate';
import { useTranslation } from 'react-i18next';
import { GetProductDataDetailResponse } from '../../../../ApiOld/Api/ApiServerVov';
import ProductCategorySelect from '../../../Shared/ProductCategorySelect';
import ManufacturerSelect from '../../../Shared/ManufacturerSelect';
import MeasureUnitSelect from '../../../Shared/MeasureUnitSelect';
import NumericField from '../../../Shared/NumericField';
import { ChangeEventArgs, HtmlEditor, Image, Inject, Link, QuickToolbar, RichTextEditorComponent, Table, Toolbar } from '@syncfusion/ej2-react-richtexteditor';
import { useEffect } from 'react';

interface ProductMainDetailProps {
  productData: Partial<GetProductDataDetailResponse>;
  isCode: boolean;
  setProductData: (data: Partial<GetProductDataDetailResponse> | null) => void;
}

export default function ProductMainDetail(props: Readonly<ProductMainDetailProps>) {
  const { productData, isCode, setProductData } = props;
  const { t, i18n } = useTranslation();

  useEffect(() => {
    return () => {
      // Cleanup RichTextEditor - odstranění přidaných elementů, které se mohou nedisposnout
      document.querySelectorAll('.e-rte-backgroundcolor-element, .e-rte-fontcolor-element').forEach(el => {
        el.remove();
      });
    };
  }, []);

  return (
    <Box>
      <ValidationGroup>
        <Box sx={{ background: 'white', p: 2, boxShadow: 3, borderRadius: '10px', width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', margin: '0px', marginTop: '16px' }}>
            <Grid container spacing={3}>
              <Grid item xs={12} lg={12} p={0}>
                <Grid container spacing={1} sx={{ ml: -1 }}>
                  <Grid item xs={12} md={6}>
                    <Validate name='code-product-validate' initialValidation={'noisy'} required={[true, t('FieldRequired')]}>
                      <TextField label={t('code')} variant="outlined" fullWidth size='small'
                        name='code'
                        value={productData.code ?? ''}
                        onChange={(e) => { setProductData({ ...productData, code: e.target.value }) }}
                        sx={{ my: 1 }}
                      />
                    </Validate>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Validate name='name-product-validate' initialValidation={'noisy'} required={[true, t('FieldRequired')]}>
                      <TextField label={t('name')} variant="outlined" fullWidth size='small'
                        name='name'
                        value={productData.name ?? ''}
                        onChange={(e) => { setProductData({ ...productData, name: e.target.value }) }}
                        sx={{ my: 1 }}
                      />
                    </Validate>
                  </Grid>
                </Grid>
              </Grid>
              <Grid container spacing={1} sx={{ ml: 2 }}>
                <Grid item xs={12} md={6}>
                  {isCode && <TextField label={'EAN'} variant="outlined" fullWidth size='small'
                    name='ean'
                    value={productData.eanCode ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || (/^\d+$/.test(value) && value.length <= 14)) {
                        setProductData({ ...productData, eanCode: value });
                      }
                    }}
                    inputProps={{
                      maxLength: 14,
                      pattern: '[0-9]*',
                      inputMode: 'numeric'
                    }}
                    sx={{ my: 1 }}
                  />}
                  {!isCode && <Validate name='ean-product-validate' initialValidation={'noisy'} required={[true, t('FieldRequired')]}>
                    <TextField label={'EAN'} variant="outlined" fullWidth size='small'
                      name='ean'
                      value={productData.eanCode ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || (/^\d+$/.test(value) && value.length <= 14)) {
                          setProductData({ ...productData, eanCode: value });
                        }
                      }}
                      inputProps={{
                        maxLength: 14,
                        pattern: '[0-9]*',
                        inputMode: 'numeric'
                      }}
                      sx={{ my: 1 }}
                    />
                  </Validate>}
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField sx={{ lineHeight: '1rem', my: 1 }} value={Number(productData.isActive)} onChange={(e) => { setProductData({ ...productData, isActive: Boolean(e.target.value) }) }} variant="outlined" fullWidth label={t('warehouses.active')} select size='small' >
                    <MenuItem sx={{ height: '20px' }} value={1}>{t('yesL')}</MenuItem>
                    <MenuItem sx={{ height: '20px' }} value={0}>{t('noL')}</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
              <Grid container spacing={1} sx={{ ml: 2 }}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ my: 1 }}>
                    <ProductCategorySelect
                      label={t('warehouses.productCategories')}
                      selectedProductCategoryId={productData.stockProductCategoryId}
                      onChange={(e) => { setProductData({ ...productData, stockProductCategoryId: e ? e.id : undefined }) }} />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ my: 1 }}>
                    <ManufacturerSelect
                      label={t('warehouses.manufacturer')}
                      selectedManufacturerId={productData.stockManufacturerId}
                      onChange={(e) => { setProductData({ ...productData, stockManufacturerId: e ? e.id : undefined }) }} />
                  </Box>
                </Grid>
              </Grid>
              <Grid container spacing={1} sx={{ ml: 2 }}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ my: 1 }}>
                    <MeasureUnitSelect
                      label={t('Unit')}
                      selectedMeasureUnitId={productData.measureUnitId}
                      onChange={(e) => { setProductData({ ...productData, measureUnitId: e ? e.id : undefined }) }} />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ my: 1 }}>
                    <NumericField
                      label={t('warehouses.unitWeight')}
                      value={productData.unitWeight}
                      onChange={(value) => setProductData({ ...productData, unitWeight: value })}
                    />
                  </Box>
                </Grid>
              </Grid>
              <Grid container spacing={1} sx={{ ml: 3, mt: 2 }}>
                <RichTextEditorComponent actionBegin={(e) => { }} height={400} locale={i18n.language} value={productData.text} change={(eve: ChangeEventArgs) => {
                  setProductData({ ...productData, text: eve.value });
                }} >
                  <Inject services={[Toolbar, Image, Link, HtmlEditor, QuickToolbar, Table]} />
                </RichTextEditorComponent>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </ValidationGroup>
    </Box>
  )
}
